import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ArrowUp, ArrowDown, Trash2, Plus, Wand2, Save, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const nid = () => Math.random().toString(36).slice(2, 12);
const move = (arr, i, d) => { const a = [...arr]; const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; };

function Row({ v, onChange, onRemove, onUp, onDown, testid, withToggle = true }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <Input value={v.label} onChange={(e) => onChange({ ...v, label: e.target.value })} placeholder="Label" className="h-8 flex-1 min-w-[140px]" />
      <Input value={v.link} onChange={(e) => onChange({ ...v, link: e.target.value })} placeholder="/category/slug or https://…" className="h-8 flex-1 min-w-[160px]" />
      {withToggle && <Switch checked={v.enabled !== false} onCheckedChange={(x) => onChange({ ...v, enabled: x })} />}
      <button onClick={onUp} className="text-slate-400 hover:text-steel"><ArrowUp size={15} /></button>
      <button onClick={onDown} className="text-slate-400 hover:text-steel"><ArrowDown size={15} /></button>
      <button onClick={onRemove} className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
    </div>
  );
}

function CategoryAdder({ cats, onAdd, testid }) {
  const [q, setQ] = useState("");
  const res = q.trim().length > 1 ? cats.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())).slice(0, 10) : [];
  return (
    <div className="relative">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="+ Add a category (type to search)" className="h-8" data-testid={testid} />
      {res.length > 0 && <div className="absolute z-30 mt-1 w-full rounded-md border bg-white shadow-lg max-h-56 overflow-y-auto">
        {res.map((c) => <button key={c.link} onClick={() => { onAdd(c); setQ(""); }} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100">{c.name} <span className="text-xs text-muted-foreground">({c.count})</span></button>)}
      </div>}
    </div>
  );
}

function Item({ it, i, cats, set, remove, up, down }) {
  const [open, setOpen] = useState(false);
  const upd = (k, v) => set({ ...it, [k]: v });
  const list = (k) => ({
    change: (j, v) => upd(k, it[k].map((x, n) => (n === j ? v : x))), remove: (j) => upd(k, it[k].filter((_, n) => n !== j)),
    move: (j, d) => upd(k, move(it[k], j, d)) });
  const ch = list("children"); const ft = list("featured");
  return (
    <div className="rounded-xl border bg-white" data-testid={`menu-item-${i}`}>
      <div className="flex flex-wrap items-center gap-2 p-3">
        <button onClick={() => setOpen(!open)} data-testid={`menu-item-toggle-${i}`}><ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} /></button>
        <Input value={it.label} onChange={(e) => upd("label", e.target.value)} className="h-9 w-56 font-semibold" data-testid={`menu-item-label-${i}`} />
        <Input value={it.link} onChange={(e) => upd("link", e.target.value)} placeholder="Optional 'Shop all' link" className="h-9 flex-1 min-w-[160px]" />
        <span className="text-xs text-muted-foreground">{it.children.length} links</span>
        <Switch checked={it.enabled} onCheckedChange={(v) => upd("enabled", v)} data-testid={`menu-item-enabled-${i}`} />
        <button onClick={up}><ArrowUp size={16} /></button><button onClick={down}><ArrowDown size={16} /></button>
        <button onClick={remove} className="text-red-600" data-testid={`menu-item-remove-${i}`}><Trash2 size={16} /></button>
      </div>
      {open && (
        <div className="border-t p-3 space-y-4 bg-slate-50/60">
          <div className="space-y-2">
            <div className="text-xs font-semibold text-steel">Panel links (subcategories) — toggle to hide</div>
            {it.children.map((c, j) => <Row key={c.id} v={c} onChange={(v) => ch.change(j, v)} onRemove={() => ch.remove(j)} onUp={() => ch.move(j, -1)} onDown={() => ch.move(j, 1)} testid={`menu-child-${i}-${j}`} />)}
            <div className="grid sm:grid-cols-2 gap-2">
              <CategoryAdder cats={cats} onAdd={(c) => upd("children", [...it.children, { id: nid(), label: c.name, link: c.link, enabled: true }])} testid={`menu-add-cat-${i}`} />
              <Button size="sm" variant="outline" onClick={() => upd("children", [...it.children, { id: nid(), label: "", link: "", enabled: true }])}>+ Custom link</Button>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-xs font-semibold text-steel">Featured links (right side of panel, e.g. Specials)</div>
            {it.featured.map((f, j) => <Row key={f.id} v={f} withToggle={false} onChange={(v) => ft.change(j, v)} onRemove={() => ft.remove(j)} onUp={() => ft.move(j, -1)} onDown={() => ft.move(j, 1)} testid={`menu-featured-${i}-${j}`} />)}
            <Button size="sm" variant="outline" onClick={() => upd("featured", [...it.featured, { id: nid(), label: "", link: "" }])} data-testid={`menu-add-featured-${i}`}>+ Featured link</Button>
          </div>
        </div>)}
    </div>
  );
}

export default function MenuBuilderTab() {
  const [m, setM] = useState(null);
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/admin/site/menu").then((r) => setM(r.data));
    api.get("/admin/site/menu/categories").then((r) => setCats(r.data));
  }, []);
  if (!m) return <p className="text-muted-foreground">Loading…</p>;
  const setItems = (items) => setM({ ...m, items });
  const save = async () => {
    try { const r = await api.put("/admin/site/menu", m); setM(r.data); toast.success("Menu saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const rebuild = async () => {
    if (!window.confirm("Replace the current menu with groups rebuilt from your categories? (Not saved until you click Save.)")) return;
    const r = await api.post("/admin/site/menu/auto"); setItems(r.data.items); toast.info("Rebuilt — review and Save");
  };
  return (
    <div className="max-w-5xl space-y-4" data-testid="menu-builder">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h1 className="text-3xl font-extrabold mb-1">Mega Menu</h1>
          <p className="text-muted-foreground">The "Shop by Category" bar under the header (desktop) and the slide-out menu (mobile).</p></div>
        <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={m.enabled} onCheckedChange={(v) => setM({ ...m, enabled: v })} data-testid="menu-enabled" /> Menu on</label>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={save} className="bg-accent text-steel hover:bg-amber-600 font-semibold" data-testid="menu-save"><Save size={16} className="mr-2" /> Save menu</Button>
        <Button variant="outline" onClick={() => setItems([...m.items, { id: nid(), label: "New menu item", link: "", enabled: true, children: [], featured: [] }])} data-testid="menu-add-item"><Plus size={16} className="mr-1" /> Add top-level item</Button>
        <Button variant="outline" onClick={rebuild} data-testid="menu-rebuild"><Wand2 size={16} className="mr-1" /> Rebuild from categories</Button>
      </div>
      {m.items.map((it, i) => (
        <Item key={it.id} it={it} i={i} cats={cats} set={(v) => setItems(m.items.map((x, n) => (n === i ? v : x)))}
          remove={() => window.confirm(`Remove "${it.label}"?`) && setItems(m.items.filter((_, n) => n !== i))}
          up={() => setItems(move(m.items, i, -1))} down={() => setItems(move(m.items, i, 1))} />))}
    </div>
  );
}
